import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["hovered"],
  _excluded2 = ["focused"],
  _excluded3 = ["aria-label", "aria-labelledby", "characterCount", "children", "displayOptimized", "error", "validationMessage", "help", "hideDisplayOptimizedBorderIndicator", "htmlFor", "hoverContent", "label", "labelTooltip", "onBlur", "readOnly", "required", "tooltip", "tooltipPlacement", "tooltipProps", "verticalSeparation", "FormLabel"],
  _excluded4 = ["focused"],
  _excluded5 = ["hovered"];
import { Children, cloneElement, useRef, forwardRef } from 'react';
import { HoverProvider, useHovered } from '../../utils/useHovered';
import { useFocused } from '../../utils/useFocused';
import { FieldsetContextConsumer } from '../../context/FieldsetContext';
import { Text } from '../../../components/Text/Text';
import useUniqueId from 'react-utils/hooks/useUniqueId';
import styled from 'foundations-theming/utils';
import { FormLabel as DefaultFormLabel } from '../../components/FormLabel/FormLabel';
import getComponentName from 'react-utils/getComponentName';
import { getCoreComponent } from '../ShareInput/ShareInput.helpers';
import devLogger from 'react-utils/devLogger';
import { Tooltip } from '../Tooltip';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { FocusProvider } from '../../utils/FocusProvider';
import { DisplayOptimizedProvider } from '../../context/DisplayOptimizedContext';
import { FormControlFocusHoverContextProvider } from '../../context/FormControlFocusHoverContext';
import { TruncateString } from '../TruncateString';
import { Flex } from '../../../components/Flex';
import { createFocusWithinStateSelector, createHoverStateSelector, getScreenReaderOnlyStyles } from '../../utils/styles/styles';
import { CSS_SPECIFICITY } from '../../constants/CSSSpecificity';
import { InfoIcon } from 'foundations-assets/icons/components/InfoIcon';
import { MERCURY_LAYER } from '../../constants';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const LabelWrapper = styled.div.withConfig({
  displayName: "FormControl__LabelWrapper"
})(({
  $displayOptimized,
  hasLabel,
  hasHelp,
  theme
}) => {
  const addPaddingBottom = !$displayOptimized && (hasLabel || hasHelp);
  return {
    paddingBottom: addPaddingBottom ? theme.space['100'] : undefined,
    paddingTop: hasHelp && !hasLabel ? theme.space['200'] : undefined
  };
});
export const StyledRequiredIndicator = styled.span.withConfig({
  displayName: "FormControl__StyledRequiredIndicator"
})([""]);
const StyledLabelContainer = styled.span.withConfig({
  displayName: "FormControl__StyledLabelContainer"
})(["&&{cursor:", ";}"], ({
  disabled
}) => disabled ? 'not-allowed' : 'default');
export const StyledInfoIconWrapper = styled.span.withConfig({
  displayName: "FormControl__StyledInfoIconWrapper"
})(["margin-inline-start:", ";vertical-align:middle;"], ({
  theme
}) => theme.space['200']);
const StyledInnerLabel = styled.div.withConfig({
  displayName: "FormControl__StyledInnerLabel"
})(["inline-size:100%;"]);
const StyledHoverContentWrapper = styled.span.withConfig({
  displayName: "FormControl__StyledHoverContentWrapper"
})(({
  $visible,
  theme
}) => {
  return Object.assign({
    position: 'absolute',
    insetBlockStart: 0,
    insetInlineEnd: 0,
    whiteSpace: 'nowrap',
    backgroundColor: theme.color['fill-surface-default'],
    boxShadow: `0 ${theme.space['50']} ${theme.space['100']} ${theme.space['50']} ${theme.color['fill-surface-default']}`,
    borderRadius: theme.borderRadius['100'],
    paddingInlineStart: theme.space['200'],
    zIndex: MERCURY_LAYER,
    // Hide when empty, to avoid seeing an empty box shadow when `hoverContent` is provided but renders no content
    '&:empty': {
      display: 'none'
    }
  }, !$visible && Object.assign({}, getScreenReaderOnlyStyles(theme)));
});

// position is relative so that hover content can have absolute positioning
const StyledFormControlHoverContentContainer = styled.div.withConfig({
  displayName: "FormControl__StyledFormControlHoverContentContainer"
})(["position:relative;"]);
const InputTooltipWrapper = ({
  labelTooltip,
  children,
  hovered,
  disabled
}) => {
  var _child$type;
  const child = Children.only(children);

  // Check if the child is already a Tooltip component
  const isChildTooltip = child.type === Tooltip || typeof child.type === 'function' && ((_child$type = child.type) === null || _child$type === void 0 ? void 0 : _child$type.displayName) === 'Tooltip';

  // Always render the same structure to prevent loss of focus on inputs, but conditionally disable tooltip behavior
  return /*#__PURE__*/_jsx(Tooltip, {
    title: labelTooltip,
    open: labelTooltip && !isChildTooltip && hovered ? true : undefined,
    placement: "top",
    disabled: disabled || !labelTooltip || isChildTooltip,
    children: child
  });
};
const getClosestDescendantId = child => {
  let element = child;
  let id = element.props.id;
  while (id == null) {
    try {
      //@ts-expect-error - keeping logic from UIComponents
      element = Children.only(element.props.children);
    } catch (e) {
      break;
    }
    id = element.props.id;
  }
  return id;
};
const getDerivedId = (inputProps, id) => {
  const IDs = Object.keys(inputProps).filter(prop => inputProps[prop]).map(prop => {
    if (!inputProps[prop]) return '';
    return `${id}-${prop}`;
  });
  if (IDs.length > 0) return IDs.join(' ');
  return undefined;
};
const getHtmlFor = (propValue, children) => {
  const firstChild = Children.only(children);
  if (propValue) {
    return propValue;
  }
  if (firstChild) {
    //@ts-expect-error - preserving logic from UIComponents
    // eslint-disable-next-line hubspot-dev/no-confusing-browser-globals
    return getClosestDescendantId(firstChild) || name;
  }
  return null;
};
const warnOnBrokenChildPropInjection = (child, htmlFor) => {
  // Source: https://html.spec.whatwg.org/multipage/forms.html#category-label
  const validFormElementTypes = ['INPUT', 'TEXTAREA', 'SELECT', 'PROGRESS', 'METER', 'OUTPUT'];
  const componentName = Children.only(child) &&
  //@ts-expect-error - preserving logic from UIComponents
  getComponentName(getCoreComponent(Children.only(child).type));
  if (componentName &&
  /*
      These components have existing issues, but we don't want to add noise to downstream devs:
        - `Select` can render a `<button>`, but we don't want to allow `<button>` since that can cause issues
          See: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/label#buttons
        - `ToggleGroup` routes the labelId to the inputs, but has the `for` on a `<div>`.
        - `CopyInput` doesn't pass remainder of unspecified props to `TextInput`.
        - `TimePicker` renders a `Select` and should also be excluded.
        - `Slider` renders a `div` and should also be excluded.
    */
  !['Select', 'ToggleGroup', 'CopyInput', 'TimePicker', 'Slider'].includes(componentName)) {
    const renderedChild = document.getElementById(htmlFor);
    if (renderedChild && !validFormElementTypes.includes(renderedChild.nodeName)) {
      devLogger.warn({
        message: 'FormControl: For accessibility props to work correctly, the direct descendant should be a valid form element.',
        key: 'FormControl: validchild'
        // url: 'https://tools.hubteamqa.com/ui-library/show/UIComponents/form/UIFormControl#uiformcontrol/prop-passing-to-child',
      });
    }
  }
};
const renderCounter = characterCount => characterCount == null ? null : /*#__PURE__*/_jsx(Text, {
  as: "small",
  children: characterCount
});
const renderHelp = (help, renderedInputId) => {
  if (!help) return null;
  return /*#__PURE__*/_jsx(Text, {
    as: "small",
    display: "block",
    typography: "heading-25",
    color: "text-core-subtle",
    id: getDerivedId({
      help
    }, renderedInputId),
    tabIndex: -1,
    truncate: false,
    children: help
  });
};
const renderInput = (ariaLabel, ariaLabelledBy, children, error, help, required, validationMessage, disabledFieldset, fallbackId, readOnly) => {
  const child = Children.only(children);
  const {
    required: origRequired,
    error: origError,
    disabled: origDisabled,
    readOnly: origReadOnly
    //@ts-expect-error - preserving logic from UIComponents
  } = child && child.props;

  // if the child input doesn't declare `required`, pass it down
  const newRequired = required === true ? true : origRequired;
  const ariaInvalid = error === true ? 'true' : undefined;
  const newError = error === true ? true : origError;
  const descendantId = getClosestDescendantId(child);
  const newDisabled = disabledFieldset === true ? true : origDisabled;
  const cloneProps = {
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    required: newRequired,
    'aria-invalid': ariaInvalid,
    'aria-required': newRequired,
    id: descendantId || fallbackId,
    error: newError,
    disabled: newDisabled
  };
  if (!descendantId) {
    cloneProps.id = fallbackId;
  }

  // only add error prop if there is an error, to avoid potential "Unknown prop" warnings
  if (error) {
    cloneProps.error = !!error;
  }

  // disable the input if we're within a disabled fieldset (#3356)
  if (disabledFieldset) {
    cloneProps.disabled = true;
  }

  // only set readOnly if it's not already defined on the child
  if (readOnly && origReadOnly == null) {
    cloneProps.readOnly = readOnly;
  }

  // aggregate IDs to pass into aria-describedby
  const newDescribedBy = getDerivedId({
    validationMessage,
    help
  }, cloneProps.id);

  // derive ID and spread back into cloneProps
  const describedCloneProps = {
    'aria-describedby': newDescribedBy
  };
  const mergedCloneProps = Object.assign({}, cloneProps, describedCloneProps);

  //@ts-expect-error - keeping logic from UIComponents
  return /*#__PURE__*/cloneElement(child, mergedCloneProps);
};
const LabelTooltipWrapper = ({
  labelTooltip,
  children,
  hovered
}) => {
  if (!labelTooltip) return children;
  return /*#__PURE__*/_jsx(Tooltip, {
    title: labelTooltip,
    open: hovered || undefined,
    placement: "top right",
    children: /*#__PURE__*/_jsx(_Fragment, {
      children: children
    })
  });
};
const HoverContent = ({
  hoverContent,
  formControlHovered,
  formControlFocused
}) => {
  const _useHovered = useHovered({}),
    {
      hovered
    } = _useHovered,
    onMouseEventHandlers = _objectWithoutPropertiesLoose(_useHovered, _excluded);
  const _useFocused = useFocused({}),
    {
      focused
    } = _useFocused,
    focusHandlers = _objectWithoutPropertiesLoose(_useFocused, _excluded2);
  const visible = formControlHovered && !formControlFocused || hovered || focused;
  return /*#__PURE__*/_jsx(StyledHoverContentWrapper, Object.assign({
    "data-test-id": "hover-content-wrapper",
    $visible: visible
  }, focusHandlers, onMouseEventHandlers, {
    children: hoverContent
  }));
};
const renderLabel = (children, label, error, htmlFor, labelId, required, tooltip, tooltipPlacement, tooltipProps, renderedInput, handleLabelClick, hovered, hoverContent, labelTargetRef, displayOptimized, labelTooltip, FormLabel) => {
  if (!label) return null;
  const {
    disabled: childDisabled
  } =
  //@ts-expect-error - keeping logic from UIComponents
  Children.only(children).props;
  const computedHtmlFor = getHtmlFor(htmlFor, renderedInput);
  if (process.env.NODE_ENV !== 'production') {
    warnOnBrokenChildPropInjection(renderedInput, computedHtmlFor);
  }
  const renderIconTooltip = () => /*#__PURE__*/_jsx(Tooltip, Object.assign({}, tooltipProps, {
    placement: tooltipPlacement,
    title: tooltip,
    children: /*#__PURE__*/_jsx(StyledInfoIconWrapper, {
      children: /*#__PURE__*/_jsx(InfoIcon, {
        color: "icon-core-subtle",
        alignWithText: true
      })
    })
  }));
  const renderLabelContent = () => {
    if (displayOptimized) {
      return /*#__PURE__*/_jsx(LabelTooltipWrapper, {
        labelTooltip: labelTooltip,
        hovered: hovered,
        children: /*#__PURE__*/_jsx(TruncateString, {
          useFlex: true,
          alignItems: "center",
          fixedChildren: /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(StyledRequiredIndicator, {}), tooltip && renderIconTooltip()]
          }),
          children: label
        })
      });
    }
    return /*#__PURE__*/_jsx(LabelTooltipWrapper, {
      labelTooltip: labelTooltip,
      hovered: hovered,
      children: /*#__PURE__*/_jsx(Flex, {
        alignItems: "center",
        children: /*#__PURE__*/_jsxs(StyledInnerLabel, {
          children: [label, /*#__PURE__*/_jsx(StyledRequiredIndicator, {}), tooltip && renderIconTooltip()]
        })
      })
    });
  };
  return /*#__PURE__*/_jsx(FormLabel, {
    color: "text-core-default",
    fullWidth: true,
    htmlFor: computedHtmlFor,
    id: labelId,
    marginBlock: displayOptimized ? '0' : '50',
    onClick: handleLabelClick,
    paddingBlockStart: "200",
    paddingBlockEnd: "0",
    required: required,
    withFormControl: true,
    error: !!error,
    children: /*#__PURE__*/_jsx(StyledLabelContainer, {
      disabled: childDisabled,
      ref: labelTargetRef,
      children: renderLabelContent()
    })
  });
};
export function validateFormControlValidationMessage({
  validationMessage,
  error
}) {
  if (validationMessage && !error) {
    devLogger.warn({
      message: 'FormControl: Use of `error={false}` with `validationMessage` is discouraged.',
      url: 'https://github.com/HubSpotEngineering/UIComponents/issues/4597',
      key: 'FormControl: `error={false}` with `validationMessage`'
    });
  }
}
const renderValidation = (error, validationMessage, inputId) => {
  if (validationMessage) {
    validateFormControlValidationMessage({
      error,
      validationMessage
    });
    return /*#__PURE__*/_jsx(Text, {
      as: "small",
      typography: "heading-25",
      id: getDerivedId({
        validationMessage
      }, inputId),
      tabIndex: -1,
      color: error ? 'text-alert-default' : 'fill-positive-pressed',
      truncate: false,
      children: validationMessage
    });
  }
  return null;
};
const StyledFormMeta = styled.div.withConfig({
  displayName: "FormControl__StyledFormMeta"
})(({
  theme
}) => {
  return {
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'nowrap',
    inlineSize: '100%',
    marginBlockStart: theme.space['100']
  };
});
const StyledFormMessages = styled.div.withConfig({
  displayName: "FormControl__StyledFormMessages"
})(() => {
  return {
    display: 'flex',
    flexDirection: 'column',
    flex: 1
  };
});
const getDisplayOptimizedBorderStyles = ($hideBorder, $error, theme) => {
  const baseStyles = {
    borderBottom: `${theme.borderWidth['100']} solid ${theme.color['fill-transparent-default']}`
  };
  if ($hideBorder) {
    return baseStyles;
  }
  if ($error) {
    return Object.assign({}, baseStyles, {
      borderBottomColor: theme.color['border-alert-default']
    });
  }
  return Object.assign({}, baseStyles, {
    [createHoverStateSelector('&:hover')]: {
      borderBottomColor: theme.color['border-interactive-default']
    },
    [createFocusWithinStateSelector('&:focus-within')]: {
      borderBottomColor: theme.color['border-info-default']
    }
  });
};
export const StyledFormControlWrapper = styled.div.withConfig({
  displayName: "FormControl__StyledFormControlWrapper"
})(({
  $verticalSeparation,
  $cssStyles,
  $displayOptimized,
  theme,
  $hideBorder,
  $error
}) => {
  const flush = $verticalSeparation === 'flush';
  const separated = $verticalSeparation === 'separated';
  const definedMarginBlockStart = ($cssStyles === null || $cssStyles === void 0 ? void 0 : $cssStyles.marginBlockStart) || ($cssStyles === null || $cssStyles === void 0 ? void 0 : $cssStyles.marginBlock) || ($cssStyles === null || $cssStyles === void 0 ? void 0 : $cssStyles.margin);
  return Object.assign({
    // make sure that !important is used only if user defined marginBlockStart is not set and flush is true
    marginBlockStart: flush && !definedMarginBlockStart ? `${theme.space['0']} !important` : separated ? theme.space[$displayOptimized ? '100' : '300'] : undefined,
    // styles form controls that follow another form control
    '& + &': {
      marginBlockStart: $displayOptimized ? theme.space['100'] : theme.space['300']
    }
  }, $displayOptimized && getDisplayOptimizedBorderStyles($hideBorder, $error, theme), {
    // user defined marginBlockStart has to be able to override default spacing between form controls
    [CSS_SPECIFICITY.FORCE_OVERRIDE]: Object.assign({}, $cssStyles)
  });
});
export const FormControl = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      characterCount,
      children,
      displayOptimized = false,
      error = false,
      validationMessage,
      help,
      hideDisplayOptimizedBorderIndicator = false,
      htmlFor,
      hoverContent,
      label,
      labelTooltip,
      onBlur,
      readOnly = false,
      required = false,
      tooltip,
      tooltipPlacement = 'top',
      tooltipProps,
      verticalSeparation,
      FormLabel = DefaultFormLabel
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded3);
  const _fallbackId = useUniqueId('FormControl');
  const _labelId = useUniqueId('FormControl-label');
  const labelTargetRef = useRef(null);
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const handleLabelClick = evt => {
    const labeledElement = evt && evt.target && document.getElementById(evt.target.htmlFor);
    const labelableTagNames = ['BUTTON', 'INPUT', 'SELECT', 'TEXTAREA'];
    if (labeledElement && labelableTagNames.indexOf(labeledElement.tagName) === -1) {
      labeledElement.click();
    }
  };
  return /*#__PURE__*/_jsx(DisplayOptimizedProvider, {
    value: displayOptimized,
    children: /*#__PURE__*/_jsx(HoverProvider, {
      children: hoverProviderProps => /*#__PURE__*/_jsx(FocusProvider, {
        onBlur: onBlur,
        children: focusProviderProps => /*#__PURE__*/_jsx(FormControlFocusHoverContextProvider, {
          focused: focusProviderProps.focused,
          hovered: hoverProviderProps.hovered,
          children: /*#__PURE__*/_jsx(FieldsetContextConsumer, {
            children: fieldsetContext => {
              const {
                  focused
                } = focusProviderProps,
                focusProviderRestProps = _objectWithoutPropertiesLoose(focusProviderProps, _excluded4);
              const {
                  hovered
                } = hoverProviderProps,
                hoverProviderRestProps = _objectWithoutPropertiesLoose(hoverProviderProps, _excluded5);
              const renderedInput = renderInput(ariaLabel, ariaLabelledBy || _labelId, children, error, help, required, validationMessage, fieldsetContext.disabled, _fallbackId, readOnly);
              const inputId = renderedInput && renderedInput.props && renderedInput.props.id;
              return /*#__PURE__*/_jsxs(StyledFormControlWrapper, Object.assign({
                $cssStyles: cssStyles,
                $verticalSeparation: verticalSeparation,
                $displayOptimized: displayOptimized,
                $hideBorder: hideDisplayOptimizedBorderIndicator || readOnly,
                $error: error
              }, focusProviderRestProps, hoverProviderRestProps, domProps, {
                ref: ref,
                children: [/*#__PURE__*/_jsxs(StyledFormControlHoverContentContainer, {
                  children: [(label !== null || help !== null) && /*#__PURE__*/_jsxs(LabelWrapper, {
                    $displayOptimized: displayOptimized,
                    hasLabel: label != null,
                    hasHelp: help != null,
                    children: [renderLabel(children, label, error, htmlFor, _labelId, required, tooltip, tooltipPlacement, tooltipProps, renderedInput, handleLabelClick, hovered, hoverContent, labelTargetRef, displayOptimized, labelTooltip, FormLabel), renderHelp(help, inputId)]
                  }), hoverContent && /*#__PURE__*/_jsx(HoverContent, {
                    hoverContent: hoverContent,
                    formControlHovered: Boolean(hovered),
                    formControlFocused: Boolean(focused)
                  }), /*#__PURE__*/_jsx(InputTooltipWrapper, {
                    labelTooltip: labelTooltip,
                    hovered: hovered,
                    disabled: !!label,
                    children: renderedInput
                  })]
                }), (validationMessage || characterCount !== undefined) && /*#__PURE__*/_jsxs(StyledFormMeta, {
                  children: [/*#__PURE__*/_jsx(StyledFormMessages, {
                    children: renderValidation(error, validationMessage, inputId)
                  }), renderCounter(characterCount)]
                })]
              }));
            }
          })
        })
      })
    })
  });
});
FormControl.displayName = 'FormControl';